import { Calendar } from 'lucide-react';
import * as React from 'react';
import styled from 'styled-components';
import AiChatView, { aiChatsFromJson, AiChatViewState, AiChatWrapperJson, AI_CHAT_HISTORY_LOCAL_STORAGE_KEY } from '../../containers_v2/ai/aiChatView';
import { CalendarEvent, CalendarEvents, EventsBoundsForMapView } from '../../containers_v2/calendar/Calendar';
import CalendarView, { CalendarViewState, HoveredEvent } from '../../containers_v2/calendar/CalendarView';
import { NewEventDefaultValues } from '../../containers_v2/calendar/popup/NewEvent';
import { NullableReactStateType } from '../../containers_v2/map/Map';
import { ModalState } from '../../containers_v2/products/model';
import { getLocalStorage } from '../../helpers/localStorage';
import { BorderColor, PastelPrimaryColor, PrimaryColor, SurfaceColor } from '../../styles/global/css/Utils';

type Panel = 'ai' | 'calendar'

interface PanelContextType {
	isPanelOpen: boolean,
	setIsPanelOpen: React.Dispatch<React.SetStateAction<PanelContextType['isPanelOpen']>>,
	disabled: boolean,
	setDisabled: React.Dispatch<React.SetStateAction<PanelContextType['disabled']>>
	calendarViewState: CalendarViewState
	aiChatHook: AiChatViewState['chatHook']
	aiStateHook: AiChatViewState['aiStateHook'],
	panel: Panel
	setPanel: React.Dispatch<React.SetStateAction<PanelContextType['panel']>>
	titleHook: NullableReactStateType<string>
}

const defaultBehaviour: PanelContextType = {
	isPanelOpen: false,
	setIsPanelOpen: () => undefined,
	disabled: false,
	setDisabled: () => undefined,
	calendarViewState: {
		eventsHook: [[], () => undefined],
		resetHook: [false, () => undefined],
		dateHook: [new Date(), () => undefined],
		mapModalHook: [undefined, () => undefined],
		eventModalHook: [{ isOpen: false }, () => undefined],
		hoveredEventHook: [undefined, () => undefined]
	},
	aiChatHook: [[], () => undefined],
	aiStateHook: [undefined, () => undefined],
	panel: 'calendar',
	setPanel: () => undefined,
	titleHook: [undefined, () => undefined]
};

export const PanelContext = React.createContext<PanelContextType>(defaultBehaviour);

const DEFAULT_PANEL_WIDTH = '450px';
const PANEL_TRANSITION_TIME = '0.5s';

export function PanelProvider(props: {
	children: React.ReactNode,
	defaultOpen?: boolean,
	defaultPanelId?: string,
}) {
	const [isPanelOpen, setIsPanelOpen] = React.useState(props.defaultOpen ?? defaultBehaviour.isPanelOpen);
	const [panel, setPanel] = React.useState<Panel>('calendar');

	const [disabled, setDisabled] = React.useState(false);
	const eventsHook = React.useState<CalendarEvents[]>([]);
	const resetHook = React.useState<boolean>(false);
	const dateHook = React.useState<Date>(new Date());
	const mapModalHook = React.useState<EventsBoundsForMapView>();
	const eventModalHook = React.useState<ModalState<{ event?: CalendarEvent, defaultValues?: NewEventDefaultValues }>>({ isOpen: false });
	const hoveredEventHook = React.useState<HoveredEvent>();

	const chatHistory = getLocalStorage<AiChatWrapperJson[]>(AI_CHAT_HISTORY_LOCAL_STORAGE_KEY) ?? [];
	const aiChatHook = React.useState<AiChatViewState['chatHook'][0]>(chatHistory.flatMap(aiChatsFromJson));
	const aiStateHook = React.useState<AiChatViewState['aiStateHook'][0]>();
	const titleHook = React.useState<string>();

	const calendarViewState: CalendarViewState = React.useMemo(() => ({
		eventsHook,
		resetHook,
		dateHook,
		mapModalHook,
		eventModalHook,
		hoveredEventHook,
	}), [eventsHook[0], resetHook[0], dateHook[0], mapModalHook[0], eventModalHook[0], hoveredEventHook[0]]);

	return <PanelContext.Provider value={{ isPanelOpen, setIsPanelOpen, disabled, setDisabled, calendarViewState, panel, setPanel, aiChatHook, aiStateHook, titleHook }}>
		{props.children}
	</PanelContext.Provider>;
}

export function PanelViewSplitter(props: {
	children: React.ReactNode,
	isMobile?: boolean
}) {
	const context = React.useContext(PanelContext);
	const { isPanelOpen, disabled, setIsPanelOpen, panel, setPanel } = context;

	if (props.isMobile) {
		return <>
			{props.children}
			<MobilePanelWrapper isOpen={isPanelOpen}>
				{panel === 'calendar' && <CalendarView panelView />}
				{panel === 'ai' && <AiChatView />}
			</MobilePanelWrapper>
		</>;
	}

	return <div style={{ display: 'flex', width: '100%', height: '100%', alignItems: 'center', overflow: 'hidden' }}>
		<div style={{ width: isPanelOpen && !disabled ? `calc(100% - ${DEFAULT_PANEL_WIDTH})` : '100%', flexShrink: 0, transition: `width ${PANEL_TRANSITION_TIME}`, height: '100%' }}>
			{props.children}
		</div>
		<PanelWrapperWrapper className='filter-modal' width={DEFAULT_PANEL_WIDTH}>
			{!disabled && (!isPanelOpen || panel === 'ai') && (
				<Langette onClick={() => {
					setPanel('calendar');
					setIsPanelOpen(true);
				}}>
					<Calendar size={18} color={PrimaryColor} />
				</Langette>
			)}
			<PanelWrapper>
				{panel === 'calendar' && <CalendarView panelView />}
				{panel === 'ai' && <AiChatView />}
			</PanelWrapper>
		</PanelWrapperWrapper>
	</div>;
}



const Langette = styled.button`
  width: 35px;
  height: 35px;
  border-radius: 50% 0 0 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 10px 0px 0;
  transition: unset;
  position: absolute;
  top: 50%;
  translate: -100% -50%;
  border: 1px solid color-mix(in oklab, ${PrimaryColor} 30%, transparent);
  background: ${PastelPrimaryColor};
  z-index: 7;
`;

const PanelWrapperWrapper = styled.div<{ width: string }>`
	height: 100%;
	background: ${SurfaceColor};
	position: relative;
	top: 0;
	flex-shrink: 0;
	transition: ${PANEL_TRANSITION_TIME};
	width: ${({ width }) => width};
`;

const PanelWrapper = styled.div`
	width: 100%;
	height: 100%;
    overflow: hidden;
    position: relative;
    border-left: 1px solid ${BorderColor};
`;

const MobilePanelWrapper = styled.div<{ isOpen: boolean }>`
	left: 0;
	width: 100%;
	position: fixed;
	transition: ${PANEL_TRANSITION_TIME};
	overflow: hidden;
	height: ${({ isOpen }) => isOpen ? '80%' : '0'};
	bottom: ${({ isOpen }) => isOpen ? '0' : '-66px'};
	background: ${SurfaceColor};
	z-index: 100;
	border-top: 1px solid ${BorderColor};
	// padding for mobile bottom floating buttons
	padding-bottom: 66px;
`;
